import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { SoftwareAdskEditor } from '@/routes/software/adsk/SoftwareAdskEditor'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { FormMode } from '@/enums'
import { LoaderFunction, redirect } from 'react-router-dom'
import { defer, makeAction } from 'react-router-typesafe'
import { HttpResponseModel, ODataClientError } from '@odata2ts/http-client-api'
import { ODataModelResponseV4 } from '@odata2ts/odata-core'
import { ad_subscription, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { Roles } from '@/auth/azure/Roles'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { createDescriptor } from '@/components/BreadCrumbs/BreadCrumbs'

export const softwareAdskRouteLoader = (async ({ request, params }: RouteFunctionParams<'clientId' | 'swAdskId'>) => {
  const qsAdminApi = useQsAdminApiManager.getState().service

  const clientId = params.clientId

  let azienda: HttpResponseModel<ODataModelResponseV4<azienda>> | undefined
  if (clientId)
    azienda = await qsAdminApi.azienda(Number(clientId)).query((builder, azienda) => {
      builder.select('id', 'nome')
    })
  let title = azienda ? `${azienda.data.nome} - ` : ``

  let swLicense: ad_subscription

  if (params.swAdskId) {
    const currentSwId = params.swAdskId
    if (Number.isNaN(currentSwId)) throw new Error('Invalid swAdskId')
    const getNewSwAdsk = await qsAdminApi
      .ad_subscription(Number(currentSwId))
      .query((builder, qqs_licenza) => {
        builder.expanding('csn', (csnBuilder, csn) => {
          csnBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
            aziendaBuilder.expand('ad_csns')
            aziendaBuilder.select('id', 'nome', 'ad_csns')
          })
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Licenza non trovata')
        throw new Error()
      })
    swLicense = getNewSwAdsk.data satisfies ad_subscription
    title +=
      swLicense.subscriptionId && swLicense.subscriptionId !== ''
        ? swLicense.subscriptionId
        : 'SubscriptionId non definito'

    const clientId = params.clientId
    if (clientId) {
      const swAdskResp = await getNewSwAdsk
      const swAdsk = swAdskResp.data satisfies ad_subscription
      if (swAdsk?.csn?.azienda && swAdsk?.csn?.azienda.id !== Number(clientId)) {
        throw new Error(
          `Il software ${swAdsk.serialNumber} appartiene al cliente ${swAdsk?.csn?.azienda.nome} e non al cliente corrente`,
        )
      }
    }
  } else {
    title += 'Nuova licenza'
    swLicense = {
      id: 0,
      subscriptionId: null,
      serialNumber: null,
      quantity: 0,
      status: null,
      startDate: null,
      endDate: null,
      extensionDate: null,
      productName: null,
      productCode: null,
      licenseType: null,
      term: null,
      autoRenew: false,
      opportunity: null,
      release: null,
      switchType: null,
      behavior: null,
      contract: null,
      note: null,
      manualEntry: false,
    }
  }

  const getClientList = qsAdminApi.azienda().query((builder, azienda) => {
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
    builder.filter(azienda.sedi.any())
    builder.filter(clientId ? azienda.id.eq(Number(clientId)) : null)
  })

  const getProductCatalog = qsAdminApi.ad_product_catalog().query((builder, product) => {
    builder.select('offeringId', 'offeringName', 'offeringCode')
    builder.orderBy(product.offeringName.asc())
  })

  return defer({
    title,
    swLicense,
    getClientList,
    getProductCatalog,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles: [Roles.GlobalAdministrator, Roles.Administrator, Roles.Supervisor],
  })
}) satisfies LoaderFunction

export const SoftwareAdskEditorRoute = {
  path: ':swAdskId',
  element: <SoftwareAdskEditor mode={FormMode.Edit} />,
  loader: softwareAdskRouteLoader,
  action: makeAction(async ({ request, params }: RouteFunctionParams<'swAdskId'>) => {
    const swAdskData = (await request.json()) as ad_subscription
    const res = await useQsAdminApiManager.getState().service.ad_subscription(swAdskData.id).patch(swAdskData)
    return null
  }),
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label:
        match.data.swLicense.subscriptionId && match.data.swLicense.subscriptionId !== ''
          ? match.data.swLicense.subscriptionId
          : 'SubscriptionId non definito',
    }),
  },
} as RoleRouteObject

export const SoftwareAdskCreatorRoute = {
  path: 'new',
  element: <SoftwareAdskEditor mode={FormMode.Create} />,
  loader: softwareAdskRouteLoader,
  index: true,
  action: makeAction(async ({ request, params }: RouteFunctionParams) => {
    const swAdskData = (await request.json()) as ad_subscription
    const res = await useQsAdminApiManager.getState().service.ad_subscription().create(swAdskData)
    return redirect(`../${res.data.id}`)
  }),
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: () => createDescriptor,
  },
} as RoleRouteObject
